import { useCallback } from 'react'
import {
  useActivateAgencyMutation,
  useSuspendAgencyMutation,
} from '@/modules/agencies/api/agenciesApi'
import type { Agency } from '@/modules/agencies/types/agency'
import {
  getAgencyStatusAction,
  type AgencyStatusAction,
} from '@/modules/agencies/utils/agencyStatusAction'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import {
  selectAgencyStatusConfirm,
  selectAgencyStatusError,
  selectIsAgencyStatusPending,
} from '@/store/selectors/agenciesUiSelectors'
import {
  statusActionFailed,
  statusActionSubmitted,
  statusActionSucceeded,
  statusConfirmClosed,
  statusConfirmOpened,
} from '@/store/slices/agenciesUiSlice'
import { getAuthErrorMessage } from '@/utils/getAuthErrorMessage'

export function useAgencyStatusActions() {
  const dispatch = useAppDispatch()
  const statusConfirm = useAppSelector(selectAgencyStatusConfirm)
  const isPending = useAppSelector(selectIsAgencyStatusPending)
  const statusError = useAppSelector(selectAgencyStatusError)
  const [suspendAgency] = useSuspendAgencyMutation()
  const [activateAgency] = useActivateAgencyMutation()

  const openStatusConfirm = useCallback(
    (agency: Agency) => {
      const action = getAgencyStatusAction(agency.status)
      if (!action) {
        return
      }

      dispatch(
        statusConfirmOpened({
          action,
          agencyId: agency.id,
          agencyName: agency.name,
        }),
      )
    },
    [dispatch],
  )

  const closeStatusConfirm = useCallback(() => {
    dispatch(statusConfirmClosed())
  }, [dispatch])

  const confirmStatusAction = useCallback(async () => {
    if (!statusConfirm) {
      return
    }

    dispatch(statusActionSubmitted())

    const result =
      statusConfirm.action === 'suspend'
        ? await suspendAgency(statusConfirm.agencyId)
        : await activateAgency(statusConfirm.agencyId)

    if (result.error) {
      dispatch(statusActionFailed(getAuthErrorMessage(result.error)))
      return
    }

    dispatch(statusActionSucceeded())
  }, [activateAgency, dispatch, statusConfirm, suspendAgency])

  return {
    statusConfirm,
    isPending,
    statusError,
    openStatusConfirm,
    closeStatusConfirm,
    confirmStatusAction,
    getAvailableAction: (status: string): AgencyStatusAction | null =>
      getAgencyStatusAction(status),
  }
}
